import axios from "axios";
import Color from "color";
import { DEFAULT_ERROR_MESSAGE } from "constants/app";
import { MONTH_LABELS } from "constants/dateTime";
import { differenceInMilliseconds } from "date-fns";
import excel from "icons/excel.svg";
import folderClosed from "icons/folder-closed.svg";
import folderOpen from "icons/folder-open.svg";
import pdf from "icons/pdf.svg";
import word from "icons/word.svg";
import { isPlainObject } from "lodash";

import { scaleThreshold } from "./scaleThreshold";

export { DATE_VARIANTS, formatDate } from "./formatDate";
export { formatList } from "./formatList";
export { formatYesNo } from "./formatYesNo";
export { default as groupBy } from "./groupBy";
export { default as handleKeyboardClick } from "./handleKeyboardClick";
export * from "./tableColumns";
export { default as uniq } from "./uniq";

export const LS_STORE_PREFIX = "vt";
export const LS_SIDEMENU_GROUPS_CACHE = `${LS_STORE_PREFIX}_sidemenu_open_groups`;

const DEFAULT_DOMAIN = [0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9];
const DEFAULT_RANGE = [
  "#E0E0E0",
  "#DBF5D6",
  "#C9F0C1",
  "#B7EBAD",
  "#A5E698",
  "#93E184",
  "#81DC6F",
  "#70D75B",
];
export const DEFAULT_COLOR_SCALE = scaleThreshold({
  domain: DEFAULT_DOMAIN,
  range: DEFAULT_RANGE,
});

export const isExcel = (document) => /\.xls[xmb]?$/i.test(document);
export const isWord = (document) => /\.(doc[xm]?|rtf)$/i.test(document);
export const isPdf = (document) => document?.toLowerCase().endsWith(".pdf");
export const isDocx = (document) => document?.toLowerCase().endsWith(".docx");

export const getImageFromType = (type) => {
  switch (type) {
    case "pdf":
      return pdf;
    case "word":
      return word;
    case "excel":
      return excel;
    case "folder-open":
      return folderOpen;
    case "folder-closed":
      return folderClosed;
    default:
      return null;
  }
};

export const getImage = (document) => {
  if (isWord(document)) {
    return word;
  }
  if (isPdf(document)) {
    return pdf;
  }
  if (isExcel(document)) {
    return excel;
  }
  return null;
};

export const getCssImage = (document) => {
  if (isWord(document)) {
    return "word-image";
  }
  if (isPdf(document)) {
    return "pdf-image";
  }
  if (isExcel(document)) {
    return "excel-image";
  }
  return null;
};

export const htmlDecode = (input) => {
  const doc = new DOMParser().parseFromString(input, "text/html");
  return doc.documentElement.textContent;
};

export const hoverSame = (index) => {
  const items = document.querySelectorAll(`.item-${index}`);

  for (const item of items) {
    item.classList.add("hovered");
  }
};
export const unhoverSame = () => {
  const items = document.querySelectorAll(".hovered");
  for (const item of items) {
    item.classList.remove("hovered");
  }
};

export const isNameValid = (name) => {
  const regex = new RegExp(/^[&a-zA-Z0-9\-()+. ]+$/i);
  return regex.test(name);
};

export const REPORT_NAME_ERROR_CODES = new Set([
  "NAME_ALREADY_EXISTS",
  "NAME_ALREADY_EXISTS_PROPOSAL",
  "NAME_ALREADY_EXISTS_MISC_ANALYSIS",
  "NAME_EMPTY",
  "NAME_INVALID_CHARACTERS",
  "NAME_TOO_LONG",
]);

/**
 * Example:
 * listOfUsers.toSorted(sortObjectArrayComparator(true, "username"))
 * @param {boolean} asc
 * @param {string} field
 */
export const sortObjectArrayComparator = (asc, field) => (a, b) => {
  if (field) {
    if (a[field] === null) {
      return asc ? -1 : 1;
    }
    if (b[field] === null) {
      return asc ? 1 : -1;
    }
    if (Array.isArray(a[field])) {
      a = a[field][0];
      b = b[field][0];
    } else {
      a = a[field];
      b = b[field];
    }
  }
  if (a === null) {
    return asc ? -1 : 1;
  }
  if (b === null) {
    return asc ? 1 : -1;
  }

  if (Number.isInteger(a) && Number.isInteger(b)) {
    if (a === b) {
      return 0;
    }
    if (asc) {
      return a < b ? -1 : 1;
    }
    return a < b ? 1 : -1;
  }

  if (a.toString().toLowerCase() === b.toString().toLowerCase()) {
    return 0;
  }
  if (asc) {
    return a.toString().toLowerCase() < b.toString().toLowerCase() ? -1 : 1;
  }
  return a.toString().toLowerCase() < b.toString().toLowerCase() ? 1 : -1;
};

export const isUserNameValid = (userName) => {
  const regex = "[/\\\\:*?\"<>|'\\s]";
  const encoded = unescape(encodeURIComponent(userName));
  const containsUTF = encoded !== userName;
  return !containsUTF && !userName.match(regex);
};

export const isWorkspaceNameValid = (workspaceName) => {
  const regex = /^[A-Za-z0-9()+\-\. ]+$/;
  const encoded = unescape(encodeURIComponent(workspaceName));
  const containsUTF = encoded !== workspaceName;
  return !containsUTF && workspaceName.match(regex);
};

export const isIntegerValid = (value) => value.match(/^\d+$/);

export const arraysEqual = (a, b) => {
  if (a === b) {
    return true;
  }

  if (a == null || b == null) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }

  // sort arrays by object values to compare order-independent
  a = a
    .slice()
    .sort((x, y) => (JSON.stringify(x) > JSON.stringify(y) ? 1 : -1));
  b = b
    .slice()
    .sort((x, y) => (JSON.stringify(x) > JSON.stringify(y) ? 1 : -1));

  // check each object for equality
  for (let i = 0; i < a.length; ++i) {
    if (JSON.stringify(a[i]) !== JSON.stringify(b[i])) {
      return false;
    }
  }
  return true;
};

/**
 * Generates a random 6 digits number.
 * useId() from React library is preferred for react components,
 * but it's only available from React v18 or higher
 * @returns {number}
 */
export const uniqueId = () => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] % 1000000;
};

const getPercentageOfNumberBetweenTwoNumbers = (
  minValue,
  maxValue,
  testValue
) => {
  const a = testValue - minValue;
  const b = maxValue - minValue;
  return (a / b) * 100;
};

export const getBackgroundColor = (maxValue, minValue, testValue) => {
  // HSV values for high colour
  const H_HIGH = 189;
  const S_HIGH = 100;
  const V_HIGH = 27;

  // HSV values for low colour
  const H_LOW = 184;
  const S_LOW = 7;
  const V_LOW = 100;

  const colorHigh = Color.hsv(H_HIGH, S_HIGH, V_HIGH);
  const colorLow = Color.hsv(H_LOW, S_LOW, V_LOW);

  let background;

  if (testValue === maxValue && testValue === minValue) {
    background = colorHigh;
  } else if (testValue === maxValue) {
    background = colorHigh;
  } else if (testValue === minValue) {
    background = colorLow;
  } else {
    const percentage =
      getPercentageOfNumberBetweenTwoNumbers(minValue, maxValue, testValue) /
      100;
    const hReturn = H_HIGH * percentage + H_LOW * (1 - percentage);
    const sReturn = S_HIGH * percentage + S_LOW * (1 - percentage);
    const vReturn = V_HIGH * percentage + V_LOW * (1 - percentage);

    background = Color.hsv(hReturn, sReturn, vReturn);
  }
  return background.hex();
};

export const getTextColour = (maxValue, minValue, testValue) => {
  const HEX_BLACK = "#000000";
  const HEX_WHITE = "#FFFFFF";
  let color;
  if (testValue === maxValue && testValue === minValue) {
    color = HEX_WHITE;
  } else if (testValue === minValue) {
    color = HEX_BLACK;
  } else if (testValue === maxValue) {
    color = HEX_WHITE;
  } else {
    const percentage = getPercentageOfNumberBetweenTwoNumbers(
      minValue,
      maxValue,
      testValue
    );
    if (percentage >= 30) {
      color = HEX_WHITE;
    } else {
      color = HEX_BLACK;
    }
  }

  return color;
};

export const renameKeys = (obj, keyMap) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => renameKeys(item, keyMap));
  }
  if (typeof obj === "object" && obj !== null) {
    const newObj = {};
    for (const key in obj) {
      const newKey = keyMap[key] || key;
      newObj[newKey] = renameKeys(obj[key], keyMap);
    }
    return newObj;
  }
  return obj;
};

export const hasVerticalScrollbar = (ref) => {
  const divElement = ref.current;

  if (divElement) {
    return divElement.scrollHeight > divElement.clientHeight;
  }
  return false;
};

export const arraysEqualWithOrder = (array1, array2) => {
  // Check if the arrays have the same length
  if (array1.length !== array2.length) {
    return false;
  }

  // Compare each object in the arrays
  for (let i = 0; i < array1.length; i++) {
    // Convert objects to JSON strings for comparison
    const obj1Str = JSON.stringify(array1[i]);
    const obj2Str = JSON.stringify(array2[i]);

    // Compare JSON strings
    if (obj1Str !== obj2Str) {
      return false;
    }
  }

  // If all objects are the same and in the same order, return true
  return true;
};

export const scrollToTop = (elementRef) => {
  elementRef?.current.scrollTo({ top: 0, behavior: "instant" });
};

export const copyToClipboard = async (textToCopy) => {
  await navigator.clipboard.writeText(textToCopy);

  alert("Link copied to clipboard!");
};

export const refreshPage = () => {
  window.location.reload(false);
};

const buildErrorMessageFromErrorResponse = (errorResponse) => {
  if (typeof errorResponse === "string") {
    if (errorResponse.includes("</html>")) {
      return DEFAULT_ERROR_MESSAGE;
    }
    return errorResponse;
  }
  if (errorResponse.message) {
    return errorResponse.message;
  }
  // Backend returns errorResponse.field which is useful for the developer, but
  // not useful for the user. So we ignore it here but do not want to remove it
  if (isPlainObject(errorResponse.error)) {
    return buildErrorMessageFromErrorResponse(errorResponse.error);
  }
  return errorResponse.error;
};

const getErrorMessageFromErrorResponse = (errorResponse) => {
  if (Array.isArray(errorResponse)) {
    return errorResponse
      .map((response) => buildErrorMessageFromErrorResponse(response))
      .join("\n");
  }
  return buildErrorMessageFromErrorResponse(errorResponse);
};

export const getErrorMessage = (error) => {
  console.error(error); // report the error to console not just to a string
  if (axios.isCancel(error)) {
    return null;
  }
  if (error?.response?.data) {
    return getErrorMessageFromErrorResponse(error.response.data);
  }
  if (error?.message) {
    return error.message;
  }
  return DEFAULT_ERROR_MESSAGE;
};

export const getSseErrorMessage = (rawError) => {
  if (!rawError) {
    return rawError;
  }
  let parsedError;
  try {
    parsedError = JSON.parse(rawError);
  } catch {
    return rawError;
  }
  return getErrorMessageFromErrorResponse(parsedError) || DEFAULT_ERROR_MESSAGE;
};

export const setPasswordExpiry = (expiry) =>
  expiry === "-1" ? "Never" : expiry;

export const hasRole = (roles, role) => {
  return roles !== undefined && roles !== null
    ? roles.split(",").includes(role)
    : false;
};

export const hasDictionaryPermissions = (roles) => hasRole(roles, "dictionary");

export const calculateDynamicHeight = (
  content,
  width,
  scratchRef,
  defaultHeight
) => {
  const height = defaultHeight;
  scratchRef.current.style.width = width;
  scratchRef.current.innerHTML = content;
  return Math.max(height, scratchRef.current.offsetHeight);
};

export const calculateDynamicWidth = (
  content,
  height,
  scratchRef,
  defaultWidth
) => {
  const width = defaultWidth;
  scratchRef.style.height = height;
  scratchRef.innerHTML = content;
  return Math.max(width, scratchRef.offsetWidth);
};

export const getScrollbarWidth = () => {
  const div = document.createElement("div");
  div.style.top = "-9999px";
  div.style.width = "100px";
  div.style.height = "100px";
  div.style.overflowY = "scroll";
  div.style.position = "absolute";
  document.body.appendChild(div);
  const width = div.offsetWidth - div.clientWidth;
  document.body.removeChild(div);
  return width;
};

export const truncateTime = (d) => {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const expiryDateValidator = (expiryDate, setExpiryDateError) => {
  let error = false;
  if (expiryDate === undefined || expiryDate.length === 0) {
    setExpiryDateError("Expiry date can't be empty");
    error = true;
  }
  if (!error) {
    if (truncateTime(expiryDate) < truncateTime(new Date())) {
      setExpiryDateError("Expiry date cannot be in the past");
    } else {
      setExpiryDateError("");
      return true;
    }
  }
  return false;
};

export const compareStringsCaseInsensitive = (s1, s2) => {
  if (typeof s1 !== "string" || typeof s2 !== "string") {
    return false;
  }
  return s1.toLowerCase() === s2.toLowerCase();
};

// https://stackoverflow.com/questions/2901102/how-to-format-a-number-with-commas-as-thousands-separators
export const numberFormatWithCommas = (number) =>
  number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","); // NOSONAR javascript:S5852 - (\d{3})+ uses a fixed-width inner quantifier — each iteration consumes exactly 3 digits with no ambiguity, making backtracking linear due to fixed-width repetition; worst-case work is O(n) linear.

/**
 * Formats an array representing a date into a formatted string.
 *
 * @function
 * @param {number[]} dateArray - An array containing the date in the format [year, month, day].
 * @param {boolean} [commaSeparated=true] - A flag indicating whether the formatted date should include a comma between
 *                                          the day-month and year (e.g., "18 Sep, 2025").
 * @returns {string} The formatted date string, either with or without commas based on the `commaSeparated` parameter.
 *
 * @example
 * const formattedDate = formatDateArray([2025, 10, 6]); // Returns "06 Oct 2025".
 */
export const formatDateArray = (() => {
  const cache = {};

  return (dateArray) => {
    // Validate input: check if it's an array
    if (!Array.isArray(dateArray)) {
      return dateArray;
    }

    const key = dateArray.join("-");
    if (cache[key]) {
      return cache[key];
    }

    const [year, month, day] = dateArray;
    const date = new Date(year, month - 1, day); // JS months are 0-based

    const options = { day: "2-digit", month: "short" };
    const dayMonth = date.toLocaleDateString("en-GB", options); // e.g. "18 Sep"

    const formatted = `${dayMonth} ${year}`;

    cache[key] = formatted;
    return formatted;
  };
})();

/**
 * Converts a date string from the "yyyy-mm-dd" format to "dd MMM yyyy" format.
 *
 * Example:
 *   "2025-11-19" -> "19 Nov 2025"
 *
 * @param {string} dateStr - The date string in ISO-like format: "yyyy-mm-dd".
 * @returns {string} The formatted date string in "dd MMM yyyy" format.
 *
 * @throws {TypeError} If the input is not a string.
 * @throws {Error} If the input does not match the "yyyy-mm-dd" format
 *                 or represents an invalid calendar date.
 */
export const formatDateYMDToDMY = (dateStr) => {
  if (!dateStr) {
    return dateStr;
  }

  if (typeof dateStr !== "string") {
    throw new TypeError("dateStr must be a string in 'yyyy-mm-dd' format.");
  }

  const parts = dateStr.split("-");
  if (parts.length !== 3) {
    throw new Error("Invalid date format. Expected 'yyyy-mm-dd'.");
  }

  const [yearStr, monthStr, dayStr] = parts;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    throw new Error(
      "Invalid date parts; year, month, and day must be integers."
    );
  }

  if (month < 1 || month > 12) {
    throw new Error("Month must be between 1 and 12.");
  }

  const monthName = MONTH_LABELS[month - 1];

  // Basic validation: check that the date is valid using the Date object
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    throw new Error("Invalid calendar date.");
  }

  return `${day} ${monthName} ${year}`;
};

/**
 * Transforms a date array [yyyy, mm, dd, ...] into a "yyyy-mm-dd" formatted string.
 * Pads month and day with a leading zero if they are single digits.
 *
 * @param {number[]} dateArray - An array where the first three elements are year, month, and day.
 * @returns {string|Array} The formatted date string "yyyy-mm-dd", or the original input if it is not an array.
 */
export const formatDateArrayToYMD = (dateArray) => {
  if (!Array.isArray(dateArray)) {
    return dateArray;
  }
  const [year, month, day] = dateArray;
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
};

/**
 * Converts [yyyy, mm, dd, hh?, mm?, ss?] array to a JavaScript Date object.
 * Month is 0-indexed for Date.
 *
 * @param {number[]} dateArray - Array with year, month, day, optionally hour, minute, second.
 * @returns {Date} The corresponding Date object.
 */
export const dateFromArray = ([year, month, day, hour = 0, min = 0, sec = 0]) =>
  new Date(year, month - 1, day, hour, min, sec);

/**
 * Returns a comparator for string fields in 'original' objects.
 * Uses localeCompare with numeric sorting and case-insensitive comparison.
 *
 * @param {string} field - The key in the 'original' object to compare.
 * @returns {function} Comparator function (a, b) => number.
 */
export const sortByLocaleString =
  (fieldOrGetter, options = { numeric: true, sensitivity: "base" }) =>
  (a, b) => {
    const get =
      typeof fieldOrGetter === "function"
        ? fieldOrGetter
        : (row) => row.original[fieldOrGetter];

    return (get(a) ?? "").localeCompare(get(b) ?? "", "en", options);
  };

/**
 * Returns a comparator for date arrays in 'original' objects.
 * Converts arrays to Date objects and compares numerically.
 * Null values are sorted to the bottom.
 *
 * @param {string} field - The key in the 'original' object containing a date array.
 * @returns {function} Comparator function (a, b) => number.
 */
export const sortByDateArray = (field) => (a, b) => {
  const aVal = a.original[field];
  const bVal = b.original[field];

  if (!aVal && !bVal) return 0;
  if (!aVal) return -1;
  if (!bVal) return 1;

  return dateFromArray(aVal) - dateFromArray(bVal);
};

export const sortByISOString = (field) => (a, b) => {
  const aValue = a.original[field];
  const bValue = b.original[field];
  if (!aValue && !bValue) return 0;
  if (!aValue) return 1;
  if (!bValue) return -1;
  return differenceInMilliseconds(aValue, bValue);
};

/**
 * Formats a JavaScript `Date` as an ISO-like calendar date string (`YYYY-MM-DD`) in **local time**.
 *
 * Notes:
 * - This uses `getFullYear()`, `getMonth()`, and `getDate()` (local timezone), not UTC.
 * - It does not validate that the `Date` is valid; pass a valid `Date` instance.
 *
 * @param {Date} date - The date to format.
 * @returns {string} The formatted date string in `YYYY-MM-DD` format.
 *
 * @example
 * formatYYYYMMDD(new Date(2026, 0, 5)); // "2026-01-05"
 */
export function formatDateToYYYYMMDD(date) {
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
